import React, { useState } from "react";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiSend,
  FiCheckCircle,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const ContactSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    smsConsent: false,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <section className="relative py-10 bg-linear-to-b from-white to-blue-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-20 left-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {t("contact_section.title")}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t("contact_section.subtitle")}
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* Left Side: Contact Info */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Why Contact Us Card */}
            <div className="bg-blue-600 rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-3">{t("contact_section.why_contact.title")}</h3>
              <ul className="space-y-3">
                {[
                  t("contact_section.why_contact.item1"),
                  t("contact_section.why_contact.item2"),
                  t("contact_section.why_contact.item3"),
                  t("contact_section.why_contact.item4"),
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-2.5 text-sm"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                  >
                    <FiCheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <span className="text-blue-50">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Contact Details */}
            <div className="space-y-3">
              <motion.div
                className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex items-center gap-3 group hover:shadow-lg transition-shadow"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <FiMapPin className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-0.5">
                    {t("contact_section.info.office_location")}
                  </h4>
                  <p className="text-gray-600 text-xs">
                    {t("contact_section.info.office_address")}
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex items-center gap-3 group hover:shadow-lg transition-shadow"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <FiClock className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-0.5">
                    {t("contact_section.info.business_hours")}
                  </h4>
                  <p className="text-gray-600 text-xs">
                    {t("contact_section.info.hours_text")}
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex items-center gap-3 group hover:shadow-lg transition-shadow"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <FiPhone className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-0.5">
                    {t("contact_section.info.support")}
                  </h4>
                  <p className="text-gray-600 text-xs">
                    {t("contact_section.info.support_text")}
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side: Contact Form */}
          <motion.div
            className="bg-white rounded-xl shadow-xl border border-gray-100 p-6"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <FiSend className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {t("contact_section.form.send_message")}
                </h3>
                <p className="text-xs text-gray-600">
                  {t("contact_section.form.message_desc")}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-1.5"
                >
                  {t("contact_section.form.name_label")}
                </label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors text-sm"
                    placeholder={t("contact_section.form.name_placeholder")}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-1.5"
                >
                  {t("contact_section.form.email_label")}
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors text-sm"
                    placeholder={t("contact_section.form.email_placeholder")}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-semibold text-gray-700 mb-1.5"
                >
                  {t("contact_section.form.phone_label")}
                </label>
                <div className="relative">
                  <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors text-sm"
                    placeholder={t("contact_section.form.phone_placeholder")}
                  />
                </div>
              </div>

              {/* SMS Consent */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    id="smsConsent"
                    name="smsConsent"
                    checked={formData.smsConsent}
                    onChange={handleInputChange}
                    className="mt-0.5 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-700 leading-relaxed">
                    {t("contact_section.form.sms_consent")}
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 px-5 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg hover:bg-blue-700 transition-colors text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSend className="w-4 h-4" />
                {t("contact_section.form.submit")}
              </motion.button>

              {/* Privacy */}
              <p className="text-xs text-center text-gray-500">
                {t("contact_section.form.privacy_text")}{" "}
                <LanguageLink
                  to="/privacy-policy"
                  className="text-blue-600 hover:underline font-medium"
                >
                  {t("footer.bottom.legal.privacy")}
                </LanguageLink>
                .
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
